import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Container
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import { useRole } from '../../contexts/RoleContext';
import DashboardService from '../../services/DashboardService';
import MetricsCard from '../../components/Dashboard/MetricsCard';
import StatusPieChart from '../../components/Dashboard/StatusPieChart';
import CountryBarChart from '../../components/Dashboard/CountryBarChart';
import OEBarChart from '../../components/Dashboard/OEBarChart';

const Dashboard = () => {
  const userRole = useRole();
  const [selectedODMType, setSelectedODMType] = useState('Bandwidth Upgrade/Downgrade');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    metrics: {},
    statusDistribution: [],
    countryDistribution: [],
    oeDistribution: []
  });

  const odmTypes = [
    'Bandwidth Upgrade/Downgrade',
  ];

  useEffect(() => {
    loadDashboardData();
  }, [selectedODMType]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading dashboard data for ODM type:', selectedODMType);

      const [metrics, statusDist, countryDist, oeDist] = await Promise.all([
        DashboardService.getDashboardMetrics(selectedODMType),
        DashboardService.getStatusDistribution(selectedODMType),
        DashboardService.getCountryDistribution(selectedODMType),
        DashboardService.getOEDistribution(selectedODMType)
      ]);

      console.log('Dashboard data loaded:');
      console.log('- Metrics:', metrics);
      console.log('- Status Distribution:', statusDist);
      console.log('- Country Distribution:', countryDist);
      console.log('- OE Distribution:', oeDist);

      setDashboardData({
        metrics,
        statusDistribution: statusDist,
        countryDistribution: countryDist,
        oeDistribution: oeDist
      });
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          sx={{ backgroundColor: '#f5f5f5' }}
        >
          <CircularProgress size={60} sx={{ color: '#003781', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#003781' }}>
            Loading Dashboard...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ 
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        py: 3
      }}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: 'white'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <DashboardIcon sx={{ fontSize: 40, color: '#003781', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#003781' }}>
                    ODM Analytics Dashboard
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Chip 
                      label={userRole.roleName} 
                      color={userRole.isAdmin ? "error" : "success"} 
                      size="small"
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {userRole.userEmail} • Last updated: {lastRefresh.toLocaleTimeString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" gap={2}>
                <FormControl variant="outlined" sx={{ minWidth: 280 }}>
                  <InputLabel>ODM Request Type</InputLabel>
                  <Select
                    value={selectedODMType}
                    onChange={(e) => setSelectedODMType(e.target.value)}
                    label="ODM Request Type"
                  >
                    {odmTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        <AssessmentIcon sx={{ mr: 1, fontSize: 20 }} />
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Tooltip title="Refresh Data">
                  <IconButton 
                    onClick={handleRefresh}
                    sx={{ 
                      bgcolor: '#003781', 
                      color: 'white',
                      '&:hover': { bgcolor: '#002561' }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography fontWeight="bold">Error loading dashboard data:</Typography>
              {error}
            </Alert>
          )}

          {/* Metrics Cards Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[
              { title: "Total Requests", value: dashboardData.metrics.total || 0, color: "primary" },
              { title: "Pending Review", value: dashboardData.metrics.pending || 0, color: "warning" },
              { title: "In Progress", value: dashboardData.metrics.inProgress || 0, color: "info" },
              { title: "Completed", value: dashboardData.metrics.completed || 0, color: "success" }
            ].map((metric, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <MetricsCard
                  title={metric.title}
                  value={metric.value}
                  color={metric.color}
                />
              </Grid>
            ))}
          </Grid>

          {/* Charts Section - Consistent Grid Layout */}
          <Grid container spacing={3}>
            {/* Status Distribution Chart */}
            <Grid item xs={12}>
              <Paper 
                elevation={2}
                sx={{ 
                  height: 500, 
                  borderRadius: 2,
                  backgroundColor: 'white',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ p: 3, pb: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <TrendingUpIcon sx={{ color: '#003781', mr: 1, fontSize: 24 }} />
                    <Typography variant="h6" fontWeight="bold" color="#003781">
                      Request Status Distribution
                    </Typography>
                  </Box>
                  <Divider />
                </Box>
                <Box sx={{ flex: 1, p: 2 }}>
                  <StatusPieChart data={dashboardData.statusDistribution} />
                </Box>
              </Paper>
            </Grid>

            {/* Country Distribution Chart */}
            <Grid item xs={12}>
              <Paper 
                elevation={2}
                sx={{ 
                  height: 500, 
                  borderRadius: 2,
                  backgroundColor: 'white',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ p: 3, pb: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography sx={{ fontSize: '24px', mr: 1 }}></Typography>
                    <Typography variant="h6" fontWeight="bold" color="#003781">
                      Geographic Distribution
                    </Typography>
                  </Box>
                  <Divider />
                </Box>
                <Box sx={{ flex: 1, p: 2 }}>
                  <CountryBarChart data={dashboardData.countryDistribution} />
                </Box>
              </Paper>
            </Grid>

            {/* OE Distribution Chart */}
            <Grid item xs={12}>
              <Paper 
                elevation={2}
                sx={{ 
                  height: 700, 
                  borderRadius: 2,
                  backgroundColor: 'white',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ p: 3, pb: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography sx={{ fontSize: '24px', mr: 1 }}></Typography>
                    <Typography variant="h6" fontWeight="bold" color="#003781">
                      Organizational Entity Distribution
                    </Typography>
                  </Box>
                  <Divider />
                </Box>
                <Box sx={{ flex: 1, p: 2 }}>
                  <OEBarChart data={dashboardData.oeDistribution} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
};

export default Dashboard;